import type { ChipColor } from "@legal-500/legal500-design-system";
import {
  FIRM_LOGO_FILL_KINDS,
  FIRM_LOGO_FILL_NOTE_REASONS,
  FIRM_LOGO_FILL_REVIEW_REASONS,
  isFirmLogoFillReason,
  type FirmLogoFillKind,
  type FirmLogoFillReason,
} from "../../../api/firmLogoFill.service.js";

type ReasonText = {
  label: string;
  explanation: string;
};

const REASON_TEXT: Record<FirmLogoFillReason, ReasonText> = {
  write: {
    label: "Will be added",
    explanation: "The firm's profile logo will be copied for this firm, website and location.",
  },
  "already-same": {
    label: "Already correct",
    explanation: "The same logo is already set, so nothing changes.",
  },
  "profile-logo-clash": {
    label: "Several profile logos",
    explanation:
      "The firm's profiles have different logos for this place, so none was copied. Choose one.",
  },
  "already-set": {
    label: "Different logo already set",
    explanation: "A different logo is already set here, so it won't be replaced.",
  },
  "no-firm-wide": {
    label: "No firm-wide logo",
    explanation: "The firm has logos for some locations but no firm-wide logo on this website.",
  },
};

export const KIND_LABELS: Record<FirmLogoFillKind, string> = {
  write: "Will be added",
  unchanged: "Already correct",
  review: "Needs checking",
  note: "For information",
};

export const REASONS_BY_KIND: Record<FirmLogoFillKind, readonly FirmLogoFillReason[]> = {
  write: ["write"],
  unchanged: ["already-same"],
  review: FIRM_LOGO_FILL_REVIEW_REASONS,
  note: FIRM_LOGO_FILL_NOTE_REASONS,
};

const KIND_COLORS: Record<FirmLogoFillKind, ChipColor> = {
  write: "success",
  unchanged: "default",
  review: "warning",
  note: "primary",
};

export const OTHER_OUTCOMES_LABEL = "Other outcomes";

const NEEDS_CHECKING_REASONS: readonly string[] = REASONS_BY_KIND.review;

export const PICKABLE_REASONS: readonly string[] = ["profile-logo-clash"];

export function isNeedsCheckingReason(reason: string): boolean {
  return NEEDS_CHECKING_REASONS.includes(reason);
}

function isFirmLogoFillKind(kind: string): kind is FirmLogoFillKind {
  return FIRM_LOGO_FILL_KINDS.some(knownKind => knownKind === kind);
}

export function reasonTextOf(reason: string): ReasonText {
  if (isFirmLogoFillReason(reason)) {
    return REASON_TEXT[reason];
  }

  return {
    label: "Other outcome",
    explanation: `This page doesn't recognise this outcome yet. Its code is "${reason}".`,
  };
}

export function kindColorOf(kind: string): ChipColor {
  return isFirmLogoFillKind(kind) ? KIND_COLORS[kind] : "default";
}

export function otherReasonsOf(
  byReason: Partial<Record<string, number>> | undefined,
  selectedReason: string | undefined,
): string[] {
  const reportedReasons = Object.entries(byReason ?? {})
    .filter(([reason, count]) => !isFirmLogoFillReason(reason) && (count ?? 0) > 0)
    .map(([reason]) => reason);

  if (
    selectedReason === undefined ||
    isFirmLogoFillReason(selectedReason) ||
    reportedReasons.includes(selectedReason)
  ) {
    return reportedReasons;
  }

  return [...reportedReasons, selectedReason];
}
